import { useTranslation } from "react-i18next";

import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { toModifiedProcessModelId } from "@/api/identifiers";
import { qk } from "@/api/query-keys";
import { notifySuccess } from "@/lib/notify";

export type CreateModelFileInput = {
  readonly processModelId: string;
  readonly fileName: string;
  readonly contents: string;
};

/**
 * モデルにファイルを**新規追加**する。
 *
 * 更新（`use-save-model-file.ts`）とは経路が違う: 新規は POST、更新は PUT で
 * 読み込み時のハッシュを添えて競合を検知する。新規に競合は無い。
 *
 * 保存先は multipart の `file` パートの**ファイル名で決まる**（URL のパスだけでは
 * 決まらない）ので必ず名前を付ける。
 */
export async function createModelFile(
  client: ApiClient,
  input: CreateModelFileInput,
): Promise<void> {
  const body = new FormData();
  body.append("file", new File([input.contents], input.fileName));

  await client.post(
    `/process-models/${toModifiedProcessModelId(input.processModelId)}/files`,
    body,
  );
}

export function useCreateModelFile(): UseMutationResult<
  void,
  Error,
  CreateModelFileInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (input: CreateModelFileInput) => createModelFile(client, input),
    onSuccess: async (_data, input) => {
      notifySuccess(t("toast.fileCreated", { name: input.fileName }));
      // モデル詳細のファイル一覧に増える
      await queryClient.invalidateQueries({
        queryKey: qk.processModels._root(),
      });
    },
  });
}
